import type { AvailabilityResponseModel, AvailabilitySlotModel } from "../../Core/Models/BookingModels";
import { EmptyStatePiece } from "../../Shared/UI/EmptyStatePiece";
import { LoadingPiece } from "../../Shared/UI/LoadingPiece";
import {
  buildCalendarWeeksFn,
  deriveSelectedSlotLabelFn,
  findSelectedDayFn,
} from "./Functions";
import {
  CalendarDayButtonPiece,
  CalendarLegendPiece,
  TimeSlotButtonPiece,
} from "./Pieces";

interface CalendarComponentProps {
  activeDate: string;
  availability: AvailabilityResponseModel | null;
  errorMessage?: string;
  isLoading: boolean;
  selectedSlotId: string;
  selectedTime: string;
  onSelectDate: (date: string) => void;
  onSelectSlot: (slot: AvailabilitySlotModel) => void;
}

export const CalendarComponent = ({
  activeDate,
  availability,
  errorMessage,
  isLoading,
  selectedSlotId,
  selectedTime,
  onSelectDate,
  onSelectSlot,
}: CalendarComponentProps) => {
  if (isLoading) {
    return <LoadingPiece label="Loading availability" />;
  }

  if (!availability || !availability.days.length) {
    return (
      <EmptyStatePiece
        title="No dates loaded"
        message={errorMessage || "Availability is not available right now."}
      />
    );
  }

  const weeks = buildCalendarWeeksFn(availability.days);
  const selectedDay = findSelectedDayFn(availability.days, activeDate);

  return (
    <div className="panel-surface studio-calendar p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase text-cmj-gold">
            Choose a date
          </p>
          <p className="mt-2 text-sm text-cmj-mist">
            {deriveSelectedSlotLabelFn(activeDate, selectedTime)}
          </p>
        </div>
        <CalendarLegendPiece source={availability.source} />
      </div>
      <div className="mt-6 grid gap-3">
        <div className="grid grid-cols-7 gap-2 text-center text-xs font-black uppercase text-cmj-mist">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>
        {weeks.map((week, index) => (
          <div className="grid grid-cols-7 gap-2" key={`${week[0]?.date || "week"}-${index}`}>
            {week.map((day, dayIndex) => (
              <CalendarDayButtonPiece
                day={day}
                isActive={day?.date === activeDate}
                key={day?.date || `empty-${index}-${dayIndex}`}
                onSelect={onSelectDate}
              />
            ))}
          </div>
        ))}
      </div>
      <div className="mt-6 rounded-md border border-white/10 bg-white/5 p-5">
        <p className="text-xs font-black uppercase text-cmj-gold">
          Available times
        </p>
        {selectedDay?.slots.length ? (
          <div className="mt-4 flex flex-wrap gap-3">
            {selectedDay.slots.map((slot) => (
              <TimeSlotButtonPiece
                isSelected={slot.id === selectedSlotId}
                key={slot.id}
                onSelect={onSelectSlot}
                slot={slot}
              />
            ))}
          </div>
        ) : (
          <p className="mt-4 text-sm leading-7 text-cmj-mist">
            Pick an available day to reveal the bookable time slots.
          </p>
        )}
      </div>
    </div>
  );
};
